import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchBillingInputs } from "@/lib/billing-query";
import {
  assembleBillingRatesFromBillableAcc,
  type BillingRatesData,
  type UserMonthBillable,
} from "@/lib/billing-rates-calc";
import { useReportingReconciliation } from "@/hooks/use-reporting-reconciliation";

export type {
  BillingRatesData,
  BillingRatesInput,
  EmployeeBillingData,
  MonthlyBillingData,
} from "@/lib/billing-rates-calc";

/**
 * Fetches billing rate data for all active employees for a given year.
 *
 * Faktureringsgrad = fakturerte timer / tilgjengelige timer × 100
 *
 * TOPP-4: den tunge billable-aggregeringen kjøres i Postgres via
 * `get_billing_rates`-RPC-en (paritetstestet mot JS-orakelet
 * accumulateBillableByUserMonth). Capacity-input (users, fravær, helligdager,
 * bekreftelser, loggede timer) hentes som før via fetchBillingInputs. Begge
 * pathene monteres gjennom assembleBillingRatesFromBillableAcc, så TV-dashboard
 * og admin-dashboard kan ikke divergere.
 */
export function useBillingRates(year: number) {
  const reporting = useReportingReconciliation(year);
  const reconciledThroughMonth = reporting.enabled
    ? reporting.reconciledThroughMonth
    : undefined;

  return useQuery({
    queryKey: ["billing-rates", year, reconciledThroughMonth ?? "legacy"],
    queryFn: async (): Promise<BillingRatesData> => {
      // includeBillable=false: billable kommer ferdig fra RPC-en, så hopp over
      // assignment-/reisetid-/hourly-pagineringen på klienten.
      const inputs = await fetchBillingInputs({ year, includeBillable: false });

      const { data: aggRows, error: rpcErr } = await supabase.rpc("get_billing_rates", {
        p_year: year,
      });
      if (rpcErr) throw rpcErr;

      const billableAcc = new Map<string, UserMonthBillable>();
      for (const r of (aggRows ?? []) as Array<{
        user_id: string;
        month: number;
        billable_hours: number | null;
        task_count: number | null;
      }>) {
        billableAcc.set(`${r.user_id}-${r.month}`, {
          hours: Number(r.billable_hours ?? 0),
          count: Number(r.task_count ?? 0),
        });
      }

      return assembleBillingRatesFromBillableAcc({
        users: inputs.users,
        billableAcc,
        timeEntries: inputs.timeEntries,
        absences: inputs.absences,
        confirmations: inputs.confirmations,
        holidays: inputs.holidays,
        year,
        now: new Date(),
        reconciledThroughMonth,
      });
    },
    staleTime: 5 * 60 * 1000,
  });
}
